import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CommandPalette, Kbd, type CommandAction } from "@fox1ck-org/ui";
import { useAuth } from "@fox1ck-org/auth/react";

import { useTrackerRoles } from "@/lib/permissions";
import { useT } from "@/lib/i18n";
import { useCanWrite } from "@/lib/permissions";
import { dayIn } from "@/lib/period";
import { carryPeriod } from "@/lib/route-tab";
import { useDebounced } from "@/lib/hooks/use-debounced";
import { useListCampaigns } from "@/lib/services/campaign-service";
import { useGetClick } from "@/lib/services/click-service";
import { useListLanders } from "@/lib/services/lander-service";
import { useListOffers } from "@/lib/services/offer-service";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { RAIL_LABEL, RAIL_PATH, type RailId } from "@/shell/places";

/** The rail's rows, in the palette's words — every place is one search away. */
const PLACES: RailId[] = ["today", "campaigns", "reports", "journal", "health", "offers", "landers", "partners", "sources", "domains", "settings"];

/** A click id is one word; anything with a space, or too short to be an id, is a name. Pure. */
export function looksLikeClickId(q: string): boolean {
  return /^[A-Za-z0-9_-]{8,64}$/.test(q);
}

/**
 * ⌘K (доска Top: «Кампания, клик, оффер, ленд»): the package's
 * `CommandPalette`, with the tracker's actions as data — what to create, where
 * to go, and what the query finds on the server: campaigns, offers and landers
 * by name, a click by its id. Nothing is fetched until something is typed.
 */
export function SearchPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useT();
  const navigate = useNavigate();
  const { search: urlSearch } = useLocation();
  const { logout } = useAuth();
  const { isPlatformAdmin } = useTrackerRoles();
  const canWrite = useCanWrite();
  const tz = useReportTimezone();
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim(), 200);
  const searching = open && q.length > 0;

  const campaigns = useListCampaigns({ search: q, limit: 6 }, { enabled: searching });
  const offers = useListOffers({ search: q, limit: 6 }, { enabled: searching });
  const landers = useListLanders({ search: q, limit: 6 }, { enabled: searching });
  const click = useGetClick(searching && looksLikeClickId(q) ? q : undefined, { lookup: true });

  const carried = carryPeriod(urlSearch);
  const actions = useMemo<CommandAction[]>(() => {
    const go = (to: string) => () => {
      navigate(to);
      onOpenChange(false);
      setQuery("");
    };
    const groups = {
      create: t("search.group.create"),
      places: t("search.group.places"),
      campaigns: t("search.group.campaigns"),
      offers: t("search.group.offers"),
      landers: t("search.group.landers"),
      clicks: t("search.group.clicks"),
      account: t("search.group.account"),
    };
    // A server row must survive the palette's own filter: the words it was
    // found by are its keywords.
    const found = [q];
    const out: CommandAction[] = [];
    if (canWrite) {
      out.push({ id: "new-campaign", group: groups.create, label: t("nav.newCampaign"), onSelect: go("/campaigns/new") });
    }
    for (const id of PLACES) {
      out.push({ id: `place-${id}`, group: groups.places, label: t(RAIL_LABEL[id]), onSelect: go(`${RAIL_PATH[id]}${carried}`) });
    }
    if (isPlatformAdmin) {
      out.push({ id: "place-admin", group: groups.places, label: t("nav.admin"), onSelect: go("/admin") });
    }
    for (const row of campaigns.data?.items ?? []) {
      const id = row.campaign?.id;
      if (!id) continue;
      out.push({
        id: `campaign-${id}`,
        group: groups.campaigns,
        label: row.campaign?.name || "—",
        detail: row.trafficSourceName || undefined,
        keywords: found,
        onSelect: go(`/campaigns/${id}${carried}`),
      });
    }
    for (const o of offers.data?.items ?? []) {
      out.push({ id: `offer-${o.id}`, group: groups.offers, label: o.name, keywords: found, onSelect: go(`/library/offers/${o.id}${carried}`) });
    }
    for (const l of landers.data?.items ?? []) {
      out.push({ id: `lander-${l.id}`, group: groups.landers, label: l.name, keywords: found, onSelect: go(`/library/landers/${l.id}`) });
    }
    const c = click.data;
    if (c?.clickId) {
      // The click opens in the journal on the day it happened, whatever the period was.
      const day = c.ts ? dayIn(new Date(c.ts), tz) : "";
      const range = day ? `&preset=custom&from=${day}&to=${day}` : "";
      out.push({
        id: `click-${c.clickId}`,
        group: groups.clicks,
        label: t("search.click", { id: c.clickId }),
        detail: c.country || undefined,
        keywords: found,
        onSelect: go(`/journal/clicks?q=${encodeURIComponent(c.clickId)}${range}`),
      });
    }
    out.push({ id: "sign-out", group: groups.account, label: t("nav.signOut"), onSelect: () => logout() });
    return out;
  }, [t, navigate, onOpenChange, q, canWrite, carried, isPlatformAdmin, campaigns.data, offers.data, landers.data, click.data, tz, logout]);

  return (
    <CommandPalette
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setQuery("");
      }}
      actions={actions}
      groups={[
        t("search.group.create"),
        t("search.group.campaigns"),
        t("search.group.clicks"),
        t("search.group.offers"),
        t("search.group.landers"),
        t("search.group.places"),
        t("search.group.account"),
      ]}
      search={query}
      onSearchChange={setQuery}
      loading={searching && (campaigns.isFetching || offers.isFetching || landers.isFetching || click.isFetching)}
      labels={{
        title: t("search.title"),
        placeholder: t("nav.search"),
        empty: t("search.empty"),
        loading: t("search.loading"),
      }}
      footer={
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          {t("search.hint.choose")}
          <Kbd>↵</Kbd>
          {t("search.hint.open")}
          <Kbd>Esc</Kbd>
          {t("search.hint.close")}
        </span>
      }
    />
  );
}
